import axios, { AxiosError,  AxiosRequestConfig } from "axios";
import { App } from "vue";

import { useConfigStore } from "~/store/configuration.store";
import { useAuthStore } from "../store/auth.store";

import Router from '~/router';
import { StationService } from "./services/station.service";
import { ForbiddenException } from "~/exceptions/forbidden.error";
import { AuthService } from "./services/auth.service";

const http = axios.create();

export default {
    install: (app: App) => {
        const configStore = useConfigStore();

        if(!configStore.loaded) {
            configStore.load();
        }
        http.defaults.baseURL = configStore.jwtEndpoint;
        
        http.interceptors.request.use((config: AxiosRequestConfig) => {

            config.headers = { ...config.headers, 'Accept': 'application/json', 'Content-Type': 'application/json' }
            if (['/auth/access_token', '/auth/access_token', '/auth/logout', '/auth/forgotten-password', '/auth/register' ].includes(config.url)) {
                return config;
            }

            const authStore = useAuthStore();

            if(!authStore.isLogged) {
                Router.push('/login');
                throw new axios.Cancel(`User not logged. Operation canceled ${config.url}`)
            }

            config.headers = {
                'Authorization': `Bearer ${authStore.accessToken}`,
            }

            return config;
        });

        http.interceptors.response.use(null, async (error: AxiosError) => {
            const originalRequest: AxiosRequestConfig & { retry?: boolean } = error.config;
            const authStore = useAuthStore();

            if (error?.response?.status === 401 && !originalRequest.retry) {
                originalRequest.retry = true;
                await authStore.useRefreshToken()
                    .catch((error) => {
                        console.error(error);
                        Router.push('/login');
                    });
                originalRequest.headers = {
                    ...originalRequest.headers,
                    'Authorization': `Bearer ${authStore.accessToken}`
                }

                return http(originalRequest);
            }

            if(error?.response?.status === 403 && error.config.url !== '/auth/access_token') {
                app.config.globalProperties.$router.push('/403');
                throw new ForbiddenException('http', `Request ${error.config.url} returned status 403`, error);
            }

            throw error;
        });

        app.config.globalProperties.http = http;
        app.config.globalProperties.$stationService = StationService.getInstance();
        app.config.globalProperties.$authService = AuthService.getInstance();
    }
};

export { http };